<template>
  <div>
    <template v-if="data">
      <div
        v-for="box in boxes || []"
        :key="box.level+'-'+box.data.id"
        :style="`padding:1.5px; position:absolute; top:${box.y0}px; left:${box.x0 * ratio}px; bottom:${windowHeight - box.y1}px; right:${windowWidth - (box.x1 * ratio)}px;`"
      >
        <v-card
          style="width:100%;height:100%;"
          :color="box.data.color"
          :dark="dark(box.data.color)"
          flat
          :title="box.data.tooltip"
          :class="{ 'disable-events': !box.data.aggs || box.data.aggs.length <= 1 }"
          @click="click(box)"
        >
          <div
            :style="`padding-top:${box.yText}px;width:100%;overflow:hidden;height:100%;`"
            class="text-center"
          >
            {{ box.data.title }}
            <br v-if="box.level === selected.length">
            <template v-else>
              -
            </template>
            <template v-if="box.y1-box.y0 > 65">
              {{ box.data.size && box.data.size.toLocaleString('fr') }} {{ config.unit }}<template />
            </template>
          </div>
        </v-card>
      </div>
    </template>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import tinycolor from 'tinycolor2'
  import TWEEN from '@tweenjs/tween.js'
  const d3 = require('d3-hierarchy')
  const seedColor = require('seed-color')

  const proportion = {
    linear: x => x,
    sqrt: x => Math.sqrt(x),
  }

  function comparePosition (b1, b2) {
    return {
      x: b2.x0 >= b1.x1 ? 1 : (b2.x1 <= b1.x0 ? -1 : 0),
      y: b2.y0 >= b1.y1 ? 1 : (b2.y1 <= b1.y0 ? -1 : 0),
    }
  }

  export default {
    data: () => ({
      boxes: null,
      selected: [],
    }),
    computed: {
      ...mapState(['data']),
      ...mapGetters(['config']),
      ratio () {
        if (this.windowWidth && this.windowHeight) return this.windowWidth / this.windowHeight
        return 1.62
      },
    },
    watch: {
      data (val) {
        const params = new URLSearchParams(window.location.search)
        if (params.get('filter') && this.data) {
          const selected = JSON.parse(`[${params.get('filter')}]`)
          this.selected = this.getSelected(this.data, selected, true)
          this.refresh(this.selected[this.selected.length - 1].data.aggs)
        } else this.refresh(val)
      },
    },
    mounted () {
      function animate (time) {
        requestAnimationFrame(animate)
        TWEEN.update(time)
      }
      requestAnimationFrame(animate)

      const params = new URLSearchParams(window.location.search)
      if (params.get('filter') && this.data) {
        const selected = JSON.parse(`[${params.get('filter')}]`)
        this.selected = this.getSelected(this.data, selected, true)
        this.refresh(this.selected[this.selected.length - 1].data.aggs)
      } else { this.refresh(this.data) }
    },
    methods: {
      refresh (aggs, reverseFrom) {
        if (!aggs) return
        const metric = this.config.calculation && this.config.calculation.type === 'sum' && this.config.calculation.field
        const data = {
          children: aggs.map((a, i) => ({
            id: i,
            title: a.value,
            size: metric ? a.metric : a.total,
            tooltip: `${a.value} - ${(metric ? a.metric : a.total).toLocaleString('fr')} ${this.config.unit}`,
            color: this.getColor(a, this.selected.length % 2 === 0),
            aggs: a.aggs,
          })),
        }
        const hierarchy = d3.hierarchy(data)
          .sum(d => proportion[this.config.proportion](d.size))
        d3.treemap()
          .size([this.windowWidth / this.ratio - 16 * this.selected.length, this.windowHeight - 40 * this.selected.length])
          .tile(d3.treemapSquarify.ratio(1))(hierarchy)

        const parents = this.selected.map((s, i) => ({
          ...s,
          x0: 8 * i,
          x1: this.windowWidth / this.ratio - 8 * i,
          y0: 32 * i,
          y1: this.windowHeight - 8 * i,
          level: i,
          yText: 6,
        }))
        this.boxes = parents.concat(hierarchy.leaves().map((s, i, arr) => {
          const from = reverseFrom && arr.find(n => n.data.id === reverseFrom.data.id)
          const pos = reverseFrom && comparePosition(from, s)
          const level = this.selected.length
          const x0 = reverseFrom && ((reverseFrom.data.id === s.data.id || pos.x < 0) ? 8 * level : (pos.x > 0 ? this.windowWidth / this.ratio - 16 * level : s.x0))
          const x1 = reverseFrom && ((reverseFrom.data.id === s.data.id || pos.x > 0) ? this.windowWidth / this.ratio - 8 * level : (pos.x < 0 ? 8 * level : s.x1))
          const y0 = reverseFrom && ((reverseFrom.data.id === s.data.id || pos.y < 0) ? 32 * level : (pos.y > 0 ? this.windowHeight - 40 * level : s.y0))
          const y1 = reverseFrom && ((reverseFrom.data.id === s.data.id || pos.y > 0) ? this.windowHeight - 8 * level : (pos.y < 0 ? 32 * level : s.y1))
          const box = {
            ...s,
            x0: reverseFrom ? x0 : (s.x0 + s.x1) / 2 + 8 * this.selected.length,
            x1: reverseFrom ? x1 : (s.x0 + s.x1) / 2 + 8 * this.selected.length,
            y0: reverseFrom ? y0 : (s.y0 + s.y1) / 2 + 32 * this.selected.length,
            y1: reverseFrom ? y1 : (s.y0 + s.y1) / 2 + 32 * this.selected.length,
            yText: 6,
            level: this.selected.length,
          }
          new TWEEN.Tween(box)
            .to({
              x0: s.x0 + 8 * this.selected.length,
              x1: s.x1 + 8 * this.selected.length,
              y0: s.y0 + 32 * this.selected.length,
              y1: s.y1 + 32 * this.selected.length,
              yText: (s.y1 - s.y0) / 2 - 32,
            }, this.config.animationDelay * 100)
            .easing(TWEEN.Easing.Quadratic.InOut)
            .start()
            .onComplete(() => {
            })
          return box
        }))
      },
      getSelected (aggs, selected, darken) {
        if (!selected.length) return []
        const metric = this.config.calculation && this.config.calculation.type === 'sum' && this.config.calculation.field
        const data = {
          children: aggs.map((a, i) => ({
            id: i,
            title: a.value,
            size: metric ? a.metric : a.total,
            tooltip: `${a.value} - ${(metric ? a.metric : a.total).toLocaleString('fr')} ${this.config.unit}`,
            color: this.getColor(a, darken),
            aggs: a.aggs,
          })),
        }
        const hierarchy = d3.hierarchy(data)
          .sum(d => proportion[this.config.proportion](d.size))
        d3.treemap()
          .size([this.windowWidth / this.ratio - 16 * this.selected.length, this.windowHeight - 40 * this.selected.length])
          .tile(d3.treemapSquarify.ratio(1))(hierarchy)
        const node = hierarchy.leaves().find(n => n.data.title === selected[0])
        return [node].concat(this.getSelected(node.data.aggs, selected.slice(1), !darken))
      },
      getColor (a, darken) {
        if (this.config.color.type === 'hash') {
          const color = tinycolor(seedColor(a.value).toHex())
          if (darken) return color.darken(20).toString()
          else return color.lighten(20).toString()
        }
        if (this.config.color.type === 'uniform') {
          const colors = this.config.color.palette.type === 'primary' ? ['primary', 'white'] : [this.config.color.palette.primary, this.config.color.palette.secondary]
          return colors[darken ? 0 : 1]
        }
      },
      dark (color) {
        return tinycolor(color).getLuminance() < 0.4
      },
      async click (box) {
        if (box.level === this.selected.length) {
          if (box.data.aggs && box.data.aggs.length > 1) {
            await this.expand(box)
            this.selected.push(box)
            this.refresh(box.data.aggs)
          }
        } else {
          await this.shrink()
          const parent = this.selected.splice(box.level, this.selected.length - box.level + 1)[0]
          this.refresh(this.selected.length ? this.selected[this.selected.length - 1].data.aggs : this.data, parent)
        }
        const params = new URLSearchParams(window.location.search)
        if (this.selected.length) {
          params.set('filter', JSON.stringify(this.selected.map(s => s.data.title)).slice(1, -1))
        } else params.delete('filter')
        window.history.replaceState(null, null, window.location.pathname + '?' + params.toString())
      },
      async expand (box) {
        return new Promise((resolve) => {
          this.boxes.forEach(b => {
            const pos = comparePosition(box, b)
            const level = b.level
            const x0 = (box.data.id === b.data.id || pos.x < 0) ? 8 * level : (pos.x > 0 ? this.windowWidth / this.ratio - 16 * level : b.x0)
            const x1 = (box.data.id === b.data.id || pos.x > 0) ? this.windowWidth / this.ratio - 8 * level : (pos.x < 0 ? 8 * level : b.x1)
            const y0 = (box.data.id === b.data.id || pos.y < 0) ? 32 * level : (pos.y > 0 ? this.windowHeight - 40 * level : b.y0)
            const y1 = (box.data.id === b.data.id || pos.y > 0) ? this.windowHeight - 8 * level : (pos.y < 0 ? 32 * level : b.y1)
            new TWEEN.Tween(b)
              .to({ x0, x1, y0, y1, yText: level <= this.selected.length ? 6 : (y1 - y0) / 2 - 32 }, this.config.animationDelay * 100)
              .easing(TWEEN.Easing.Quadratic.InOut)
              .start().onComplete(box => resolve(box))
          })
        })
      },
      async shrink () {
        return new Promise((resolve) => {
          this.boxes.filter(b => b.level === this.selected.length).forEach(b => {
            new TWEEN.Tween(b)
              .to({
                x0: (b.x0 + b.x1) / 2 + 8 * this.selected.length,
                x1: (b.x0 + b.x1) / 2 + 8 * this.selected.length,
                y0: (b.y0 + b.y1) / 2 + 32 * this.selected.length,
                y1: (b.y0 + b.y1) / 2 + 32 * this.selected.length,
                yText: 6,
              }, this.config.animationDelay * 100)
              .easing(TWEEN.Easing.Quadratic.InOut)
              .start().onComplete(box => resolve(box))
          })
        })
      },
    },
  }
</script>

<style>
.v-application {
  font-family: "Nunito", sans-serif!important;
}

.disable-events {
  pointer-events: none
}

html {
  overflow-y: hidden!important;
}
</style>
